import React, { useEffect, useState } from 'react';
import { Settings, Save, Globe, Clock, DollarSign } from 'lucide-react';
import { tenantAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'INR', 'SGD', 'AED'];
const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland'
];

const SettingsPage = () => {
  const { user, isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('general');

  useEffect(() => {
    tenantAPI.getSettings()
      .then(res => setSettings(res.data.data))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await tenantAPI.updateSettings({
        name: settings.name,
        phone: settings.phone,
        website: settings.website,
        address: settings.address,
        settings: settings.settings
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (path, value) => {
    setSettings(prev => {
      const parts = path.split('.');
      if (parts.length === 1) return { ...prev, [path]: value };
      if (parts.length === 2) return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: value } };
      if (parts.length === 3) return {
        ...prev,
        [parts[0]]: { ...prev[parts[0]], [parts[1]]: { ...prev[parts[0]][parts[1]], [parts[2]]: value } }
      };
      return prev;
    });
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!settings) return <div className="empty-state"><h3>Settings not available</h3></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Company Settings</h1>
          <p className="page-subtitle">Configure your company profile and preferences</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><div className="spinner" style={{ borderTopColor: '#fff' }} /> Saving...</> : <><Save size={15} /> Save Settings</>}
          </button>
        )}
      </div>

      <div className="tabs">
        {[['general', 'Company'], ['booking', 'Booking'], ['branding', 'Branding']].map(([key, label]) => (
          <button key={key} className={`tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Company Information</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-grid form-grid-2">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Company Name</label>
                <input className="form-input" value={settings.name || ''} onChange={e => set('name', e.target.value)} disabled={!isAdmin} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={settings.phone || ''} onChange={e => set('phone', e.target.value)} disabled={!isAdmin} />
              </div>
              <div className="form-group">
                <label className="form-label">Website</label>
                <input type="url" className="form-input" value={settings.website || ''} onChange={e => set('website', e.target.value)} disabled={!isAdmin} placeholder="https://" />
              </div>
            </div>
            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Business Address</div>
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Street</label>
                  <input className="form-input" value={settings.address?.street || ''} onChange={e => set('address.street', e.target.value)} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-input" value={settings.address?.city || ''} onChange={e => set('address.city', e.target.value)} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label className="form-label">State / Province</label>
                  <input className="form-input" value={settings.address?.state || ''} onChange={e => set('address.state', e.target.value)} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input className="form-input" value={settings.address?.country || ''} onChange={e => set('address.country', e.target.value)} disabled={!isAdmin} />
                </div>
                <div className="form-group">
                  <label className="form-label">ZIP / Postal Code</label>
                  <input className="form-input" value={settings.address?.zip || ''} onChange={e => set('address.zip', e.target.value)} disabled={!isAdmin} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'booking' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Booking Preferences</span></div>
          <div className="form-grid form-grid-2" style={{ gap: 20 }}>
            <div className="form-group">
              <label className="form-label"><DollarSign size={14} style={{ display: 'inline' }} /> Currency</label>
              <select className="form-select" value={settings.settings?.currency || 'USD'} onChange={e => set('settings.currency', e.target.value)} disabled={!isAdmin}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label"><Globe size={14} style={{ display: 'inline' }} /> Timezone</label>
              <select className="form-select" value={settings.settings?.timezone || 'America/New_York'} onChange={e => set('settings.timezone', e.target.value)} disabled={!isAdmin}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label"><Clock size={14} style={{ display: 'inline' }} /> Default Check-in Time</label>
              <input type="time" className="form-input" value={settings.settings?.checkInTime || '15:00'} onChange={e => set('settings.checkInTime', e.target.value)} disabled={!isAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label"><Clock size={14} style={{ display: 'inline' }} /> Default Check-out Time</label>
              <input type="time" className="form-input" value={settings.settings?.checkOutTime || '11:00'} onChange={e => set('settings.checkOutTime', e.target.value)} disabled={!isAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">Booking Confirmation</label>
              <select className="form-select" value={settings.settings?.bookingConfirmationRequired ? 'true' : 'false'}
                onChange={e => set('settings.bookingConfirmationRequired', e.target.value === 'true')} disabled={!isAdmin}>
                <option value="false">Instant Booking (no approval needed)</option>
                <option value="true">Manual Approval Required</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {tab === 'branding' && (
        <div className="card">
          <div className="card-header"><span className="card-title">White-Label Branding</span></div>
          {settings.subscription?.plan !== 'enterprise' ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⭐</div>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Enterprise Feature</h3>
              <p style={{ color: '#6B7280', marginBottom: 16 }}>White-label branding is available on the Enterprise plan.</p>
              <button className="btn btn-primary" onClick={() => window.location.href = '/billing'}>Upgrade to Enterprise</button>
            </div>
          ) : (
            <div className="form-grid form-grid-2" style={{ gap: 20 }}>
              <div className="form-group">
                <label className="form-label">Primary Color</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={settings.settings?.whiteLabel?.primaryColor || '#3B82F6'}
                    onChange={e => set('settings.whiteLabel.primaryColor', e.target.value)}
                    style={{ width: 48, height: 36, padding: 2, borderRadius: 6, border: '1px solid #E5E7EB', cursor: 'pointer' }} />
                  <input className="form-input" value={settings.settings?.whiteLabel?.primaryColor || '#3B82F6'}
                    onChange={e => set('settings.whiteLabel.primaryColor', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Accent Color</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={settings.settings?.whiteLabel?.accentColor || '#10B981'}
                    onChange={e => set('settings.whiteLabel.accentColor', e.target.value)}
                    style={{ width: 48, height: 36, padding: 2, borderRadius: 6, border: '1px solid #E5E7EB', cursor: 'pointer' }} />
                  <input className="form-input" value={settings.settings?.whiteLabel?.accentColor || '#10B981'}
                    onChange={e => set('settings.whiteLabel.accentColor', e.target.value)} />
                </div>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Custom Domain</label>
                <input className="form-input" placeholder="app.yourcompany.com"
                  value={settings.settings?.whiteLabel?.customDomain || ''}
                  onChange={e => set('settings.whiteLabel.customDomain', e.target.value)} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
